import React, { useEffect, useState } from "react";
import { Music, Calendar as CalendarIcon, ExternalLink, FileText, Image as ImageIcon, Video, Link as LinkIcon, Download, Award, DollarSign, Package as PackageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const KIND_ICONS = {
  audio: Music,
  video: Video,
  image: ImageIcon,
  pdf: FileText,
  link: LinkIcon,
  release: Award,
};

const KIND_LABELS = {
  audio: "Audio",
  video: "Video",
  image: "Immagine",
  pdf: "PDF / Documento",
  link: "Link",
  release: "Release",
};

export default function CRMArtistDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/crm/artist/me")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-[#0A0A0A]/50">Caricamento…</div>;
  if (!data) return <div className="p-8 text-[#0A0A0A]/50">Impossibile caricare i dati.</div>;

  const notLinked = !data.profile;

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="crm-artist-dashboard">
      {/* Hero header */}
      <div className="border-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7] p-8 mb-8 flex items-start gap-6 flex-wrap">
        <div className="w-24 h-24 border-2 border-[#FF5A1F] bg-[#1A1A1A] flex items-center justify-center overflow-hidden shrink-0">
          {data.profile?.image_url ? (
            <img src={data.profile.image_url} alt={data.profile.name} className="w-full h-full object-cover" />
          ) : (
            <Music size={40} className="text-[#FF5A1F]" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">— Area Riservata Artista</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase mt-1">
            {data.profile?.name || user?.name || user?.email}
          </h1>
          {data.profile?.genre_it && (
            <div className="text-sm text-[#FAFAF7]/60 mt-2 uppercase tracking-widest">{data.profile.genre_it}</div>
          )}
        </div>
      </div>

      {notLinked && (
        <div className="mb-8 border-2 border-amber-500/40 bg-amber-50 p-4" data-testid="artist-not-linked">
          <div className="font-bold text-amber-900">Profilo non ancora collegato</div>
          <p className="text-sm text-amber-800/90 mt-1">
            L'admin di Perla Music non ha ancora collegato il tuo account al tuo profilo pubblico. Contatta il team.
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <StatCard icon={PackageIcon} label="Materiali consegnati" value={data.stats?.materials_count || 0} testId="artist-stats-materials" />
        <StatCard icon={Award} label="Contratti attivi" value={data.stats?.contracts_count || 0} testId="artist-stats-contracts" accent />
        <StatCard icon={DollarSign} label="Investimento totale" value={`€${(data.stats?.revenue || 0).toLocaleString('it-IT')}`} testId="artist-stats-revenue" />
      </div>

      {/* Upcoming appointments */}
      <section className="mb-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-3xl uppercase text-[#0A0A0A]">Prossimi appuntamenti</h2>
        </div>
        {(!data.appointments || data.appointments.length === 0) ? (
          <div className="border-2 border-dashed border-[#0A0A0A]/20 p-6 text-center text-[#0A0A0A]/50 text-sm" data-testid="artist-appointments-empty">
            Nessun appuntamento fissato al momento.
          </div>
        ) : (
          <div className="grid gap-3">
            {data.appointments.map((a, i) => (
              <div key={i} className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-4 flex items-center gap-4 flex-wrap">
                <CalendarIcon size={22} className="text-[#FF5A1F] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{new Date(a.calendar_start).toLocaleString("it-IT", { dateStyle: "full", timeStyle: "short" })}</div>
                  <div className="text-xs text-[#0A0A0A]/60 uppercase tracking-widest mt-0.5">con Perla Music</div>
                </div>
                <div className="flex gap-2">
                  {a.calendar_event_link && (
                    <a href={a.calendar_event_link} target="_blank" rel="noopener noreferrer" className="btn-outline-dark py-1.5 px-3 text-xs">
                      <CalendarIcon size={12} /> Calendar
                    </a>
                  )}
                  {a.calendar_meet_link && (
                    <a href={a.calendar_meet_link} target="_blank" rel="noopener noreferrer" className="btn-cta py-1.5 px-3 text-xs">
                      <Video size={12} /> Meet
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Materials */}
      <section className="mb-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-3xl uppercase text-[#0A0A0A]">Materiali</h2>
          <span className="text-xs text-[#0A0A0A]/50">{data.materials?.length || 0} elementi</span>
        </div>
        {(!data.materials || data.materials.length === 0) ? (
          <div className="border-2 border-dashed border-[#0A0A0A]/20 p-6 text-center text-[#0A0A0A]/50 text-sm" data-testid="artist-materials-empty">
            Ancora nessun materiale caricato dal team.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data.materials.map((m) => {
              const Icon = KIND_ICONS[m.kind] || FileText;
              return (
                <div key={m.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-5" data-testid={`material-${m.id}`}>
                  <div className="flex items-start justify-between">
                    <Icon size={22} className="text-[#FF5A1F]" strokeWidth={1.5} />
                    <span className="text-[9px] uppercase tracking-widest bg-[#0A0A0A] text-[#FAFAF7] px-1.5 py-0.5 font-bold">{KIND_LABELS[m.kind] || m.kind}</span>
                  </div>
                  <h3 className="mt-3 font-bold text-lg text-[#0A0A0A] leading-tight">{m.title}</h3>
                  {m.description && <p className="mt-2 text-sm text-[#0A0A0A]/70 leading-relaxed">{m.description}</p>}
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-[#0A0A0A]/40">
                    {new Date(m.created_at).toLocaleDateString("it-IT")} · {m.uploaded_by_name || "team"}
                  </div>
                  {(m.url || m.data_url) && (
                    <a href={m.url || m.data_url} target="_blank" rel="noopener noreferrer" download={m.data_url ? m.title : undefined} className="btn-cta w-full mt-4 py-2 text-xs">
                      {m.data_url ? <><Download size={12} /> Scarica</> : <><ExternalLink size={12} /> Apri</>}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Contracts */}
      {data.contracts && data.contracts.length > 0 && (
        <section>
          <h2 className="font-display text-3xl uppercase text-[#0A0A0A] mb-4">Storico contratti</h2>
          <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
                <tr>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Data</th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Stato</th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Totale</th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Pagamento</th>
                </tr>
              </thead>
              <tbody>
                {data.contracts.map((c, i) => (
                  <tr key={i} className="border-t border-[#0A0A0A]/10">
                    <td className="px-4 py-3 text-xs">{new Date(c.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="px-4 py-3 text-xs uppercase font-bold">{c.status}</td>
                    <td className="px-4 py-3 font-bold text-[#FF5A1F]">€{(c.packages_total || 0).toLocaleString('it-IT')}</td>
                    <td className="px-4 py-3 text-xs">{c.payment_status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testId, accent }) => (
  <div className="border-2 border-[#0A0A0A] p-5 bg-[#FAFAF7]" data-testid={testId}>
    <Icon size={22} strokeWidth={1.5} className={accent ? "text-[#FF5A1F]" : "text-[#0A0A0A]"} />
    <div className="mt-3 font-display text-4xl leading-none text-[#0A0A0A]">{value}</div>
    <div className="mt-2 text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">{label}</div>
  </div>
);
